import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useUITranslation } from '../../hooks';
import styles from './ui-qr-scanner.module.scss';
import QrScanner from 'qr-scanner';
import QrFrame from './qr-frame.svg';
import { UIText } from '../ui-text';
import { UISpinner } from '../ui-spinner';
import {
    UIFilterGroup,
    UIMultiFilterGroup,
    UIMultiFilterItem,
    UIToast
} from '../..';

type UIQRScannerProps = {
    onScan: (data: string) => void;
    /**
     * Minimum time, in milliseconds, between successive onScan calls. The
     * underlying library detects a code many times per second while it is in
     * view; this throttles those down to one onScan per cooldown window so
     * consumers aren't spammed (e.g. a failed activation re-firing in rapid
     * succession), while still allowing the same code to be re-scanned later
     * to retry. Defaults to 3000ms.
     */
    scanCooldownMs?: number;
};

export function UIQRScanner(props: UIQRScannerProps): ReactElement {
    const { t } = useUITranslation();
    const { onScan, scanCooldownMs = 3000 } = props;

    // QR States
    const scanner = useRef<QrScanner>(null);
    const [scannerLoading, setScannerLoading] = useState(true);
    const [scannerError, setScannerError] = useState(false);
    const [cameras, setCameras] = useState<QrScanner.Camera[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<string>('environment');

    const videoEl = useRef<HTMLVideoElement>(null);
    const qrBoxEl = useRef<HTMLDivElement>(null);

    // The scanner callback is created once; keep the latest handler and
    // cooldown in refs so it always reads current values without being
    // recreated, and never re-fires merely because the handler's identity
    // changed on a consumer re-render.
    const onScanRef = useRef(onScan);
    useEffect(() => {
        onScanRef.current = onScan;
    }, [onScan]);

    const scanCooldownRef = useRef(scanCooldownMs);
    useEffect(() => {
        scanCooldownRef.current = scanCooldownMs;
    }, [scanCooldownMs]);

    // Timestamp of the last accepted scan, used to throttle the library's
    // rapid repeat detections down to one onScan per cooldown window.
    const lastScanAtRef = useRef(0);

    useEffect(() => {
        if (videoEl?.current && !scanner.current) {
            scanner.current = new QrScanner(
                videoEl.current,
                (result: QrScanner.ScanResult) => {
                    if (!result?.data) {
                        return;
                    }

                    // Leading-edge throttle: fire immediately, then ignore
                    // further detections until the cooldown window elapses.
                    const now = Date.now();
                    if (now - lastScanAtRef.current < scanCooldownRef.current) {
                        return;
                    }
                    lastScanAtRef.current = now;

                    onScanRef.current(result.data);
                },
                {
                    preferredCamera: 'environment'
                }
            );

            QrScanner.listCameras(true)
                .then((cameras) => {
                    setCameras(cameras);

                    let preferredCamera: string = 'environment';
                    if (cameras.length > 0) {
                        preferredCamera = cameras[0].id;
                    }
                    setSelectedCamera(preferredCamera);

                    return scanner?.current?.start();
                })
                .then(() => {
                    setScannerLoading(false);
                    setScannerError(false);
                })
                .catch(() => {
                    setScannerLoading(false);
                    setScannerError(true);
                });
        }

        return () => {
            // eslint-disable-next-line
            if (!videoEl?.current) {
                scanner?.current?.stop();
            }
        };
    }, []);

    useEffect(() => {
        if (!scannerLoading && scannerError)
            UIToast.error('Camera is blocked or not accessible.');
    }, [scannerLoading, scannerError]);

    useEffect(() => {
        if (!scannerLoading && !scannerError) {
            void scanner.current?.setCamera(selectedCamera);
        }
    }, [selectedCamera, scannerLoading, scannerError]);

    const cameraFilterOptions: UIMultiFilterGroup[] = useMemo(() => {
        return [
            {
                name: t('components.ui_qr_scanner.camera'),
                type: 'single',
                items: cameras.map((camera) => ({
                    name: camera.label,
                    id: camera.id,
                    selected: selectedCamera === camera.id
                }))
            }
        ];
    }, [selectedCamera, cameras, t]);

    const selectedCameraName = useMemo(() => {
        const selectedItem = cameraFilterOptions[0].items.find(
            (item) => item.selected
        );
        if (selectedItem) {
            return selectedItem.name;
        } else {
            return t('components.ui_qr_scanner.unknown_camera');
        }
    }, [cameraFilterOptions, t]);

    return (
        <div>
            <div className={styles.qrReader}>
                {scannerError && (
                    <div className={styles.stateContainer}>
                        <UIText>{t('components.ui_qr_scanner.blocked')}</UIText>
                        <UIText type="default2">
                            Please allow camera in your browser permissions and
                            Reload.
                        </UIText>
                    </div>
                )}
                {scannerLoading && (
                    <div className={styles.stateContainer}>
                        <UISpinner />
                    </div>
                )}
                <video ref={videoEl} />
                <div ref={qrBoxEl} className={styles.qrBox}>
                    <img
                        src={QrFrame}
                        alt="Qr Frame"
                        width={256}
                        height={256}
                        className={styles.qrFrame}
                    />
                </div>

                {!scannerLoading && !scannerError && (
                    <div className={styles.cameraPickerContainer}>
                        <UIFilterGroup>
                            <UIMultiFilterItem
                                icon="camera"
                                text={selectedCameraName}
                                groups={cameraFilterOptions}
                                popoverWidth={300}
                                onChanged={(newGroups) => {
                                    const selectedItem =
                                        newGroups[0].items.find(
                                            (item) => item.selected
                                        );
                                    if (selectedItem) {
                                        setSelectedCamera(selectedItem.id);
                                    }
                                }}
                            />
                        </UIFilterGroup>
                    </div>
                )}
            </div>
        </div>
    );
}
